export interface GeoPosition3D {
  latitude: number;
  longitude: number;
  height: number;
}

export interface CameraPoseSample extends GeoPosition3D {
  timestamp: number;
  attitudeHead: number;
  gimbalPitch: number;
  gimbalRoll: number;
  gimbalYaw: number;
}

export interface PoiLocation {
  id: string;
  name: string;
  color: string;
  latitude: number;
  longitude: number;
  height: number | null;
}

export interface CameraCalibration {
  diagonalFovDeg: number;
  yawOffsetDeg: number;
  pitchOffsetDeg: number;
  rollOffsetDeg: number;
  timeOffsetMs: number;
}

export interface VideoDisplayRect {
  left: number;
  top: number;
  width: number;
  height: number;
  containerWidth: number;
  containerHeight: number;
  sourceWidth: number;
  sourceHeight: number;
}

export interface EnuVector {
  east: number;
  north: number;
  up: number;
}

export interface CameraVector {
  x: number;
  y: number;
  z: number;
}

export interface NormalizedProjection {
  x: number;
  y: number;
  inside: boolean;
}

export interface PoiProjection {
  id: string;
  name: string;
  color: string;
  distance: number;
  x: number;
  y: number;
}

export interface VideoPoint {
  x: number;
  y: number;
}

export interface GroundProjection extends GeoPosition3D {
  distance: number;
  videoX: number;
  videoY: number;
}

export interface LiveViewDelayLike {
  video_id?: unknown;
  videoId?: unknown;
  liveview_delay_time?: unknown;
  liveviewDelayTime?: unknown;
}

export type GeojsonCoordinate3D = [number, number, (number | null)?]

const WGS84_A = 6378137
const WGS84_E2 = 6.6943799901413165e-3
const WGS84_B = WGS84_A * Math.sqrt(1 - WGS84_E2)
const WGS84_EP2 = (WGS84_A * WGS84_A - WGS84_B * WGS84_B) / (WGS84_B * WGS84_B)
const DEG_TO_RAD = Math.PI / 180
const RAD_TO_DEG = 180 / Math.PI
const DEFAULT_MAX_SAMPLE_GAP_MS = 750
const DEFAULT_LIVE_WARMUP_GAP_MS = 5000

export const POI_CAMERA_CALIBRATIONS: Record<string, CameraCalibration> = {
  '98-0-0': {
    diagonalFovDeg: 84,
    yawOffsetDeg: 0,
    pitchOffsetDeg: 0,
    rollOffsetDeg: 0,
    timeOffsetMs: 0,
  },
  '99-0-0': {
    diagonalFovDeg: 82,
    yawOffsetDeg: 0,
    pitchOffsetDeg: 0,
    rollOffsetDeg: 0,
    timeOffsetMs: 0,
  },
}

export function finiteNumber (value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

export function normalizeTimestamp (value: unknown, fallback = Date.now()): number {
  const timestamp = finiteNumber(value)
  if (timestamp === null || timestamp <= 0) {
    return fallback
  }
  if (timestamp > 1e15) {
    return timestamp / 1000
  }
  if (timestamp < 1e11) {
    return timestamp * 1000
  }
  return timestamp
}

export function createCameraPoseSample (data: Record<string, unknown>, timestamp: unknown): CameraPoseSample | null {
  const latitude = finiteNumber(data.latitude)
  const longitude = finiteNumber(data.longitude)
  const height = finiteNumber(data.height) ?? finiteNumber(data.altitude)
  const attitudeHead = finiteNumber(data.attitude_head)
  const gimbalPitch = finiteNumber(data.gimbal_pitch)
  const gimbalRoll = finiteNumber(data.gimbal_roll)
  const gimbalYaw = finiteNumber(data.gimbal_yaw)
  if (latitude === null || longitude === null || height === null || attitudeHead === null ||
    gimbalPitch === null || gimbalRoll === null || gimbalYaw === null ||
    latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null
  }
  return {
    timestamp: normalizeTimestamp(timestamp),
    latitude,
    longitude,
    height,
    attitudeHead,
    gimbalPitch,
    gimbalRoll,
    gimbalYaw,
  }
}

export function normalizeAngleDeg (angle: number): number {
  const normalized = ((angle + 180) % 360 + 360) % 360 - 180
  return Object.is(normalized, -0) ? 0 : normalized
}

export function interpolateAngleDeg (start: number, end: number, ratio: number): number {
  const delta = normalizeAngleDeg(end - start)
  return normalizeAngleDeg(start + delta * ratio)
}

export function interpolateCameraPose (
  start: CameraPoseSample,
  end: CameraPoseSample,
  targetTimestamp: number
): CameraPoseSample {
  const duration = end.timestamp - start.timestamp
  const ratio = duration <= 0 ? 0 : Math.min(1, Math.max(0, (targetTimestamp - start.timestamp) / duration))
  const interpolate = (from: number, to: number) => from + (to - from) * ratio
  return {
    timestamp: targetTimestamp,
    latitude: interpolate(start.latitude, end.latitude),
    longitude: interpolate(start.longitude, end.longitude),
    height: interpolate(start.height, end.height),
    attitudeHead: interpolateAngleDeg(start.attitudeHead, end.attitudeHead, ratio),
    gimbalPitch: interpolateAngleDeg(start.gimbalPitch, end.gimbalPitch, ratio),
    gimbalRoll: interpolateAngleDeg(start.gimbalRoll, end.gimbalRoll, ratio),
    gimbalYaw: interpolateAngleDeg(start.gimbalYaw, end.gimbalYaw, ratio),
  }
}

export function selectCameraPose (
  samples: CameraPoseSample[],
  targetTimestamp: number,
  maxSampleGapMs = DEFAULT_MAX_SAMPLE_GAP_MS
): CameraPoseSample | null {
  if (samples.length === 0 || !Number.isFinite(targetTimestamp)) {
    return null
  }
  const ordered = [...samples].sort((a, b) => a.timestamp - b.timestamp)
  const first = ordered[0]
  const last = ordered[ordered.length - 1]
  if (targetTimestamp <= first.timestamp) {
    return first.timestamp - targetTimestamp <= maxSampleGapMs ? { ...first, timestamp: targetTimestamp } : null
  }
  if (targetTimestamp >= last.timestamp) {
    return targetTimestamp - last.timestamp <= maxSampleGapMs ? { ...last, timestamp: targetTimestamp } : null
  }
  for (let index = 1; index < ordered.length; index += 1) {
    const end = ordered[index]
    if (end.timestamp < targetTimestamp) {
      continue
    }
    const start = ordered[index - 1]
    if (targetTimestamp - start.timestamp > maxSampleGapMs || end.timestamp - targetTimestamp > maxSampleGapMs) {
      return null
    }
    return interpolateCameraPose(start, end, targetTimestamp)
  }
  return null
}

/**
 * Selects the pose that belongs to a delayed live frame. When a cockpit has
 * only just started collecting DRC samples, the requested frame can predate
 * the first sample by the live-view delay. In that one warm-up direction the
 * oldest available pose is the safest approximation; future/stale samples
 * are still rejected by the strict selector.
 */
export function selectCameraPoseForLiveView (
  samples: CameraPoseSample[],
  targetTimestamp: number,
  maxSampleGapMs = DEFAULT_MAX_SAMPLE_GAP_MS,
  warmupGapMs = DEFAULT_LIVE_WARMUP_GAP_MS
): CameraPoseSample | null {
  const selected = selectCameraPose(samples, targetTimestamp, maxSampleGapMs)
  if (selected || samples.length === 0 || !Number.isFinite(targetTimestamp)) {
    return selected
  }
  const first = samples.reduce((oldest, sample) => sample.timestamp < oldest.timestamp ? sample : oldest)
  const last = samples.reduce((latest, sample) => sample.timestamp > latest.timestamp ? sample : latest)
  const warmupGap = first.timestamp - targetTimestamp
  if (warmupGap <= 0 || warmupGap > warmupGapMs || last.timestamp - first.timestamp >= warmupGapMs) {
    return null
  }
  return { ...first, timestamp: targetTimestamp }
}

export function scaleCameraCalibrationForZoom (
  calibration: CameraCalibration,
  zoomFactor: unknown
): CameraCalibration {
  const factor = finiteNumber(zoomFactor)
  if (factor === null || factor <= 1) {
    return { ...calibration }
  }
  const halfDiagonalFov = calibration.diagonalFovDeg * DEG_TO_RAD / 2
  return {
    ...calibration,
    diagonalFovDeg: 2 * Math.atan(Math.tan(halfDiagonalFov) / factor) * RAD_TO_DEG,
  }
}

export function resolvePoiHeight (poiHeight: unknown, dockHeight: unknown): number | null {
  const explicitHeight = normalizePoiHeightForStorage(poiHeight)
  if (explicitHeight !== null) {
    return explicitHeight
  }
  return finiteNumber(dockHeight)
}

export function normalizePoiHeightForStorage (value: unknown): number | null {
  const height = finiteNumber(value)
  return height === null || height === 0 ? null : height
}

export function resolveLiveViewDelay (
  videoId: string,
  delayItems: LiveViewDelayLike[] | undefined,
  playerLatencyMs: unknown
): number | null {
  const normalizedVideoId = String(videoId || '')
  const cameraPath = normalizedVideoId.split('/').slice(0, 2).join('/')
  const candidates = Array.isArray(delayItems) ? delayItems : []
  const itemDelay = (item: LiveViewDelayLike) => {
    const delay = finiteNumber(item.liveview_delay_time ?? item.liveviewDelayTime)
    return delay !== null && delay >= 0 ? delay : null
  }
  const itemVideoId = (item: LiveViewDelayLike) => String(item.video_id ?? item.videoId ?? '')
  const exactDelay = candidates
    .filter(item => itemVideoId(item) === normalizedVideoId)
    .map(itemDelay)
    .find(delay => delay !== null)
  if (exactDelay !== undefined) {
    return exactDelay
  }
  if (cameraPath) {
    const compatibleDelay = candidates
      .filter(item => itemVideoId(item).split('/').slice(0, 2).join('/') === cameraPath)
      .map(itemDelay)
      .find(delay => delay !== null)
    if (compatibleDelay !== undefined) {
      return compatibleDelay
    }
  }
  const playerDelay = finiteNumber(playerLatencyMs)
  return playerDelay !== null && playerDelay >= 0 ? playerDelay : null
}

export function withPreservedAltitude (
  original: GeojsonCoordinate3D,
  transformed: [number, number]
): GeojsonCoordinate3D {
  if (original.length < 3) {
    return [transformed[0], transformed[1]]
  }
  return [transformed[0], transformed[1], original[2]]
}

export function geodeticToEcef (position: GeoPosition3D): { x: number; y: number; z: number } {
  const latitude = position.latitude * DEG_TO_RAD
  const longitude = position.longitude * DEG_TO_RAD
  const sinLatitude = Math.sin(latitude)
  const cosLatitude = Math.cos(latitude)
  const sinLongitude = Math.sin(longitude)
  const cosLongitude = Math.cos(longitude)
  const primeVerticalRadius = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLatitude * sinLatitude)
  return {
    x: (primeVerticalRadius + position.height) * cosLatitude * cosLongitude,
    y: (primeVerticalRadius + position.height) * cosLatitude * sinLongitude,
    z: (primeVerticalRadius * (1 - WGS84_E2) + position.height) * sinLatitude,
  }
}

export function geodeticToEnu (origin: GeoPosition3D, target: GeoPosition3D): EnuVector {
  const originEcef = geodeticToEcef(origin)
  const targetEcef = geodeticToEcef(target)
  const dx = targetEcef.x - originEcef.x
  const dy = targetEcef.y - originEcef.y
  const dz = targetEcef.z - originEcef.z
  const latitude = origin.latitude * DEG_TO_RAD
  const longitude = origin.longitude * DEG_TO_RAD
  const sinLatitude = Math.sin(latitude)
  const cosLatitude = Math.cos(latitude)
  const sinLongitude = Math.sin(longitude)
  const cosLongitude = Math.cos(longitude)
  return {
    east: -sinLongitude * dx + cosLongitude * dy,
    north: -sinLatitude * cosLongitude * dx - sinLatitude * sinLongitude * dy + cosLatitude * dz,
    up: cosLatitude * cosLongitude * dx + cosLatitude * sinLongitude * dy + sinLatitude * dz,
  }
}

export function ecefToGeodetic (position: { x: number; y: number; z: number }): GeoPosition3D | null {
  if (![position.x, position.y, position.z].every(Number.isFinite)) {
    return null
  }
  const longitude = Math.atan2(position.y, position.x)
  const horizontalRadius = Math.hypot(position.x, position.y)
  if (horizontalRadius === 0 && position.z === 0) {
    return null
  }
  const theta = Math.atan2(position.z * WGS84_A, horizontalRadius * WGS84_B)
  const sinTheta = Math.sin(theta)
  const cosTheta = Math.cos(theta)
  const latitude = Math.atan2(
    position.z + WGS84_EP2 * WGS84_B * sinTheta * sinTheta * sinTheta,
    horizontalRadius - WGS84_E2 * WGS84_A * cosTheta * cosTheta * cosTheta
  )
  const sinLatitude = Math.sin(latitude)
  const cosLatitude = Math.cos(latitude)
  const primeVerticalRadius = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLatitude * sinLatitude)
  const height = Math.abs(cosLatitude) > 1e-12
    ? horizontalRadius / cosLatitude - primeVerticalRadius
    : Math.abs(position.z) - WGS84_B
  const result = {
    latitude: latitude * RAD_TO_DEG,
    longitude: longitude * RAD_TO_DEG,
    height,
  }
  return result.latitude >= -90 && result.latitude <= 90 && result.longitude >= -180 && result.longitude <= 180
    ? result
    : null
}

export function enuToGeodetic (origin: GeoPosition3D, vector: EnuVector): GeoPosition3D | null {
  const originEcef = geodeticToEcef(origin)
  const latitude = origin.latitude * DEG_TO_RAD
  const longitude = origin.longitude * DEG_TO_RAD
  const sinLatitude = Math.sin(latitude)
  const cosLatitude = Math.cos(latitude)
  const sinLongitude = Math.sin(longitude)
  const cosLongitude = Math.cos(longitude)
  const dx = -sinLongitude * vector.east - sinLatitude * cosLongitude * vector.north +
    cosLatitude * cosLongitude * vector.up
  const dy = cosLongitude * vector.east - sinLatitude * sinLongitude * vector.north +
    cosLatitude * sinLongitude * vector.up
  const dz = cosLatitude * vector.north + sinLatitude * vector.up
  return ecefToGeodetic({
    x: originEcef.x + dx,
    y: originEcef.y + dy,
    z: originEcef.z + dz,
  })
}

export function enuToCameraVector (
  vector: EnuVector,
  yawDeg: number,
  pitchDeg: number,
  rollDeg: number
): CameraVector {
  const yaw = yawDeg * DEG_TO_RAD
  const pitch = pitchDeg * DEG_TO_RAD
  const roll = rollDeg * DEG_TO_RAD
  const sinYaw = Math.sin(yaw)
  const cosYaw = Math.cos(yaw)
  const sinPitch = Math.sin(pitch)
  const cosPitch = Math.cos(pitch)
  const sinRoll = Math.sin(roll)
  const cosRoll = Math.cos(roll)

  const forward = {
    east: cosPitch * sinYaw,
    north: cosPitch * cosYaw,
    up: sinPitch,
  }
  const baseRight = {
    east: cosYaw,
    north: -sinYaw,
    up: 0,
  }
  const baseDown = {
    east: sinPitch * sinYaw,
    north: sinPitch * cosYaw,
    up: -cosPitch,
  }
  const right = {
    east: baseRight.east * cosRoll + baseDown.east * sinRoll,
    north: baseRight.north * cosRoll + baseDown.north * sinRoll,
    up: baseRight.up * cosRoll + baseDown.up * sinRoll,
  }
  const down = {
    east: baseDown.east * cosRoll - baseRight.east * sinRoll,
    north: baseDown.north * cosRoll - baseRight.north * sinRoll,
    up: baseDown.up * cosRoll - baseRight.up * sinRoll,
  }
  const dot = (axis: EnuVector) => vector.east * axis.east + vector.north * axis.north + vector.up * axis.up
  return {
    x: dot(right),
    y: dot(down),
    z: dot(forward),
  }
}

export function cameraToEnuVector (
  vector: CameraVector,
  yawDeg: number,
  pitchDeg: number,
  rollDeg: number
): EnuVector {
  const yaw = yawDeg * DEG_TO_RAD
  const pitch = pitchDeg * DEG_TO_RAD
  const roll = rollDeg * DEG_TO_RAD
  const sinYaw = Math.sin(yaw)
  const cosYaw = Math.cos(yaw)
  const sinPitch = Math.sin(pitch)
  const cosPitch = Math.cos(pitch)
  const sinRoll = Math.sin(roll)
  const cosRoll = Math.cos(roll)
  const forward = {
    east: cosPitch * sinYaw,
    north: cosPitch * cosYaw,
    up: sinPitch,
  }
  const baseRight = {
    east: cosYaw,
    north: -sinYaw,
    up: 0,
  }
  const baseDown = {
    east: sinPitch * sinYaw,
    north: sinPitch * cosYaw,
    up: -cosPitch,
  }
  const right = {
    east: baseRight.east * cosRoll + baseDown.east * sinRoll,
    north: baseRight.north * cosRoll + baseDown.north * sinRoll,
    up: baseRight.up * cosRoll + baseDown.up * sinRoll,
  }
  const down = {
    east: baseDown.east * cosRoll - baseRight.east * sinRoll,
    north: baseDown.north * cosRoll - baseRight.north * sinRoll,
    up: baseDown.up * cosRoll - baseRight.up * sinRoll,
  }
  return {
    east: vector.x * right.east + vector.y * down.east + vector.z * forward.east,
    north: vector.x * right.north + vector.y * down.north + vector.z * forward.north,
    up: vector.x * right.up + vector.y * down.up + vector.z * forward.up,
  }
}

export function diagonalFovToTangents (diagonalFovDeg: number, aspectRatio: number): { horizontal: number; vertical: number } {
  const diagonalTangent = Math.tan(diagonalFovDeg * DEG_TO_RAD / 2)
  const vertical = diagonalTangent / Math.sqrt(aspectRatio * aspectRatio + 1)
  return {
    horizontal: vertical * aspectRatio,
    vertical,
  }
}

export function projectCameraVector (
  vector: CameraVector,
  diagonalFovDeg: number,
  aspectRatio: number
): NormalizedProjection | null {
  if (!Number.isFinite(vector.z) || vector.z <= 0 || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return null
  }
  const tangents = diagonalFovToTangents(diagonalFovDeg, aspectRatio)
  const x = 0.5 + vector.x / (2 * vector.z * tangents.horizontal)
  const y = 0.5 + vector.y / (2 * vector.z * tangents.vertical)
  return {
    x,
    y,
    inside: x >= 0 && x <= 1 && y >= 0 && y <= 1,
  }
}

export function calculateCoverRect (
  containerWidth: number,
  containerHeight: number,
  sourceWidth: number,
  sourceHeight: number
): VideoDisplayRect {
  const safeContainerWidth = Math.max(0, containerWidth)
  const safeContainerHeight = Math.max(0, containerHeight)
  const safeSourceWidth = sourceWidth > 0 ? sourceWidth : 16
  const safeSourceHeight = sourceHeight > 0 ? sourceHeight : 9
  const scale = safeContainerWidth > 0 && safeContainerHeight > 0
    ? Math.max(safeContainerWidth / safeSourceWidth, safeContainerHeight / safeSourceHeight)
    : 0
  const width = safeSourceWidth * scale
  const height = safeSourceHeight * scale
  return {
    left: (safeContainerWidth - width) / 2,
    top: (safeContainerHeight - height) / 2,
    width,
    height,
    containerWidth: safeContainerWidth,
    containerHeight: safeContainerHeight,
    sourceWidth: safeSourceWidth,
    sourceHeight: safeSourceHeight,
  }
}

export function unprojectVideoPointToGround (
  point: VideoPoint,
  pose: CameraPoseSample,
  groundHeight: number,
  calibration: CameraCalibration,
  videoRect: VideoDisplayRect
): GroundProjection | null {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(groundHeight) ||
    videoRect.width <= 0 || videoRect.height <= 0 || videoRect.sourceWidth <= 0 || videoRect.sourceHeight <= 0) {
    return null
  }
  const videoX = (point.x - videoRect.left) / videoRect.width
  const videoY = (point.y - videoRect.top) / videoRect.height
  if (videoX < 0 || videoX > 1 || videoY < 0 || videoY > 1) {
    return null
  }
  const tangents = diagonalFovToTangents(
    calibration.diagonalFovDeg,
    videoRect.sourceWidth / videoRect.sourceHeight
  )
  const rayEnu = cameraToEnuVector({
    x: (videoX - 0.5) * 2 * tangents.horizontal,
    y: (videoY - 0.5) * 2 * tangents.vertical,
    z: 1,
  },
  pose.gimbalYaw + calibration.yawOffsetDeg,
  pose.gimbalPitch + calibration.pitchOffsetDeg,
  pose.gimbalRoll + calibration.rollOffsetDeg)
  const heightDelta = groundHeight - pose.height
  if (Math.abs(rayEnu.up) < 1e-9) {
    return null
  }
  const distanceScale = heightDelta / rayEnu.up
  if (!Number.isFinite(distanceScale) || distanceScale <= 0) {
    return null
  }
  const targetVector = {
    east: rayEnu.east * distanceScale,
    north: rayEnu.north * distanceScale,
    up: heightDelta,
  }
  const target = enuToGeodetic(pose, targetVector)
  if (!target) {
    return null
  }
  return {
    latitude: target.latitude,
    longitude: target.longitude,
    height: groundHeight,
    distance: Math.hypot(targetVector.east, targetVector.north, targetVector.up),
    videoX,
    videoY,
  }
}

export function projectPoiToVideo (
  poi: PoiLocation,
  pose: CameraPoseSample,
  dockHeight: number | null,
  calibration: CameraCalibration,
  videoRect: VideoDisplayRect
): PoiProjection | null {
  const height = resolvePoiHeight(poi.height, dockHeight)
  if (height === null || videoRect.width <= 0 || videoRect.height <= 0) {
    return null
  }
  const enu = geodeticToEnu(pose, {
    latitude: poi.latitude,
    longitude: poi.longitude,
    height,
  })
  const distance = Math.hypot(enu.east, enu.north, enu.up)
  const cameraVector = enuToCameraVector(
    enu,
    pose.gimbalYaw + calibration.yawOffsetDeg,
    pose.gimbalPitch + calibration.pitchOffsetDeg,
    pose.gimbalRoll + calibration.rollOffsetDeg
  )
  const normalized = projectCameraVector(
    cameraVector,
    calibration.diagonalFovDeg,
    videoRect.sourceWidth / videoRect.sourceHeight
  )
  if (!normalized?.inside) {
    return null
  }
  const x = videoRect.left + normalized.x * videoRect.width
  const y = videoRect.top + normalized.y * videoRect.height
  if (x < 0 || x > videoRect.containerWidth || y < 0 || y > videoRect.containerHeight) {
    return null
  }
  return {
    id: poi.id,
    name: poi.name,
    color: poi.color,
    distance,
    x,
    y,
  }
}
